import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/tr';
import {
	BoutiqueCalendarDateFormat,
	BoutiqueCalendarDayCount,
	BoutiqueCalendarShortDayNameList,
	BoutiqueCalendarDefaultEndHour,
	BoutiqueCalendarDefaultStartHour,
	BoutiqueCalendarLocale,
	BoutiqueCalendarRangeSeparator,
	BoutiqueCalendarTimeSeparator,
	BoutiqueEventPopoverArrowInset,
	BoutiqueEventPopoverGap,
	BoutiqueEventPopoverPlacement,
	BoutiqueEventPopoverTexts,
	BoutiqueEventPopoverSummarySeparator,
	BoutiqueEventPopoverUngroupedGroupId,
	BoutiqueEventPopoverViewportPadding
} from './boutique-calendar.constants';
import {
	BoutiqueCalendarEvent,
	BoutiqueCalendarLayoutEvent,
	BoutiqueCalendarNowPosition,
	BoutiqueCalendarWeekDay,
	BoutiqueEventGroup,
	BoutiqueEventParticipant,
	BoutiqueEventPopoverPosition
} from './boutique-calendar.types';
import { ClassroomGroup } from '../../boutique-classroom.types';

dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

const MinuteCountInHour = 60;
const HourLabelLength = 2;
const HourLabelPadCharacter = '0';
const HourLabelSuffix = ':00';
const TimePartSeparator = ':';
const EmptyLaneIndex = -1;
const NextHourStep = 1;

type DateInput = Dayjs | Date | string;

const tr = (date: DateInput = dayjs()): Dayjs => dayjs(date).locale(BoutiqueCalendarLocale);

export const getBoutiqueWeekStartDate = (referenceDate: DateInput = dayjs()): Dayjs => tr(referenceDate).startOf('isoWeek');

export const buildBoutiqueWeekDayList = (weekStartDate: DateInput, referenceDate: DateInput = dayjs()): BoutiqueCalendarWeekDay[] => {
	const todayKey = tr(referenceDate).format(BoutiqueCalendarDateFormat.key);

	return Array.from({ length: BoutiqueCalendarDayCount }, (_item, dayIndex) => {
		const date = tr(weekStartDate).add(dayIndex, 'day');
		const key = date.format(BoutiqueCalendarDateFormat.key);

		return {
			key,
			dayNumber: date.format(BoutiqueCalendarDateFormat.dayNumber),
			dayName: BoutiqueCalendarShortDayNameList[dayIndex],
			isToday: key === todayKey
		};
	});
};

export const formatBoutiqueWeekRange = (weekStartDate: DateInput): string => {
	const startDate = tr(weekStartDate);
	const endDate = tr(weekStartDate).add(BoutiqueCalendarDayCount - 1, 'day');
	const endLabel = endDate.format(BoutiqueCalendarDateFormat.dayWithMonthAndYear);

	if (!startDate.isSame(endDate, 'year')) {
		return `${startDate.format(BoutiqueCalendarDateFormat.dayWithMonthAndYear)}${BoutiqueCalendarRangeSeparator}${endLabel}`;
	}

	if (!startDate.isSame(endDate, 'month')) {
		return `${startDate.format(BoutiqueCalendarDateFormat.dayWithMonth)}${BoutiqueCalendarRangeSeparator}${endLabel}`;
	}

	return `${startDate.format(BoutiqueCalendarDateFormat.dayNumber)}${BoutiqueCalendarRangeSeparator}${endLabel}`;
};

export const parseBoutiqueTimeToMinute = (time: string): number => {
	const [hour, minute] = String(time ?? '').split(TimePartSeparator);

	return Number(hour || 0) * MinuteCountInHour + Number(minute || 0);
};

export const formatBoutiqueTimeRange = (startTime: string, endTime: string): string => `${startTime}${BoutiqueCalendarTimeSeparator}${endTime}`;

export const formatBoutiqueHourLabel = (hour: number): string => `${String(hour).padStart(HourLabelLength, HourLabelPadCharacter)}${HourLabelSuffix}`;

export const buildBoutiqueHourList = (eventList: BoutiqueCalendarEvent[] = []): number[] => {
	const minuteList = eventList.flatMap(event => [parseBoutiqueTimeToMinute(event.startTime), parseBoutiqueTimeToMinute(event.endTime)]);

	const startHour = Math.min(BoutiqueCalendarDefaultStartHour, ...minuteList.map(minute => Math.floor(minute / MinuteCountInHour)));
	const endHour = Math.max(BoutiqueCalendarDefaultEndHour, ...minuteList.map(minute => Math.ceil(minute / MinuteCountInHour)));

	return Array.from({ length: Math.max(endHour - startHour, 0) }, (_item, hourIndex) => startHour + hourIndex);
};

export const groupBoutiqueEventListByDate = (eventList: BoutiqueCalendarEvent[] = []): Record<string, BoutiqueCalendarEvent[]> =>
	eventList.reduce<Record<string, BoutiqueCalendarEvent[]>>((dateMap, event) => ({ ...dateMap, [event.dateKey]: [...(dateMap[event.dateKey] ?? []), event] }), {});

export const buildBoutiqueDayEventLayoutList = (eventList: BoutiqueCalendarEvent[] = [], startHour = 0): BoutiqueCalendarLayoutEvent[] => {
	const gridStartMinute = startHour * MinuteCountInHour;

	type TimedEvent = BoutiqueCalendarEvent & { startMinute: number; endMinute: number };

	const sortedList: TimedEvent[] = eventList
		.map(event => ({
			...event,
			startMinute: parseBoutiqueTimeToMinute(event.startTime),
			endMinute: parseBoutiqueTimeToMinute(event.endTime)
		}))
		.sort((first, second) => first.startMinute - second.startMinute || first.endMinute - second.endMinute);

	const layoutList: BoutiqueCalendarLayoutEvent[] = [];
	let clusterList: TimedEvent[] = [];
	let clusterEndMinute: number | null = null;

	const pushCluster = (): void => {
		const laneEndMinuteList: number[] = [];

		const clusterLayoutList = clusterList.map(event => {
			const availableLaneIndex = laneEndMinuteList.findIndex(laneEndMinute => laneEndMinute <= event.startMinute);
			const laneIndex = availableLaneIndex === EmptyLaneIndex ? laneEndMinuteList.length : availableLaneIndex;

			laneEndMinuteList[laneIndex] = event.endMinute;

			return {
				...event,
				laneIndex,
				topRatio: (event.startMinute - gridStartMinute) / MinuteCountInHour,
				spanRatio: Math.max(event.endMinute - event.startMinute, 0) / MinuteCountInHour
			};
		});

		clusterLayoutList.forEach(event => layoutList.push({ ...event, laneCount: laneEndMinuteList.length }));

		clusterList = [];
		clusterEndMinute = null;
	};

	sortedList.forEach(event => {
		if (clusterList.length && clusterEndMinute !== null && event.startMinute >= clusterEndMinute) {
			pushCluster();
		}

		clusterList.push(event);
		clusterEndMinute = Math.max(clusterEndMinute ?? event.endMinute, event.endMinute);
	});

	if (clusterList.length) {
		pushCluster();
	}

	return layoutList;
};

export const getBoutiqueNowPosition = (hourList: number[] = [], referenceTime: DateInput = dayjs()): BoutiqueCalendarNowPosition | null => {
	const [startHour] = hourList;
	const endHour = hourList[hourList.length - 1] + NextHourStep;

	if (startHour === undefined) {
		return null;
	}

	const now = tr(referenceTime);
	const nowMinute = now.hour() * MinuteCountInHour + now.minute();

	if (nowMinute < startHour * MinuteCountInHour || nowMinute > endHour * MinuteCountInHour) {
		return null;
	}

	return {
		label: now.format(BoutiqueCalendarDateFormat.time),
		topRatio: (nowMinute - startHour * MinuteCountInHour) / MinuteCountInHour
	};
};

export const getBoutiqueInitials = (fullName: string): string =>
	fullName
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part.charAt(0).toLocaleUpperCase(BoutiqueCalendarLocale))
		.join('');

export const isBoutiqueEventPast = (event: BoutiqueCalendarEvent, referenceTime: DateInput = dayjs()): boolean => {
	const end = dayjs(`${event.dateKey} ${event.endTime}`, `${BoutiqueCalendarDateFormat.key} ${BoutiqueCalendarDateFormat.time}`);
	return end.isValid() && end.isBefore(dayjs(referenceTime));
};

export const formatBoutiqueEventDate = (dateKey: string): string => dayjs(dateKey, BoutiqueCalendarDateFormat.key).locale(BoutiqueCalendarLocale).format(BoutiqueCalendarDateFormat.dayNameWithMonth);

export const formatBoutiqueParticipantCount = (participantCount?: number): string => `${participantCount ?? 0} ${BoutiqueEventPopoverTexts.participantCountSuffix}`;

export const normalizeBoutiqueName = (value: string): string => value.trim().toLocaleLowerCase(BoutiqueCalendarLocale);

/**
 * Groups the event's registered participants by the event's groups.
 * The event response only carries group names and participant names, so group membership
 * is resolved against the expert's loaded groups; participants that match none of the
 * event's groups are collected under a single ungrouped entry.
 */
export const buildBoutiqueEventGroupList = (event: BoutiqueCalendarEvent, groupList: ClassroomGroup[] = []): BoutiqueEventGroup[] => {
	const participantList = event.participantList ?? [];
	const participantNameSet = new Set(participantList.map(participant => normalizeBoutiqueName(participant.name)));
	const assignedNameSet = new Set<string>();

	const eventGroupList = (event.groupNameList ?? []).map<BoutiqueEventGroup>(groupName => {
		const group = groupList.find(item => normalizeBoutiqueName(item.name) === normalizeBoutiqueName(groupName));
		const groupStudentList: BoutiqueEventParticipant[] = (group?.students ?? [])
			.filter(student => !participantList.length || participantNameSet.has(normalizeBoutiqueName(student.fullName)))
			.map(student => ({ id: student.id, name: student.fullName, imageUrl: student.avatarUrl }));

		groupStudentList.forEach(student => assignedNameSet.add(normalizeBoutiqueName(student.name)));

		return { id: group?.id ?? BoutiqueEventPopoverUngroupedGroupId, name: groupName, studentList: groupStudentList };
	});

	const ungroupedStudentList = participantList.filter(participant => !assignedNameSet.has(normalizeBoutiqueName(participant.name)));

	if (ungroupedStudentList.length) {
		eventGroupList.push({ id: BoutiqueEventPopoverUngroupedGroupId, name: BoutiqueEventPopoverTexts.ungroupedStudents, studentList: ungroupedStudentList });
	}

	return eventGroupList;
};

export const countBoutiqueGroupStudents = (groupList: BoutiqueEventGroup[]): number => groupList.reduce((total, group) => total + group.studentList.length, 0);

export const formatBoutiqueStudentCount = (studentCount: number): string => `${studentCount} ${BoutiqueEventPopoverTexts.studentCountSuffix}`;

export const formatBoutiqueGroupSummary = (groupCount: number, studentCount: number): string =>
	`${groupCount} ${BoutiqueEventPopoverTexts.groupCountSuffix}${BoutiqueEventPopoverSummarySeparator}${formatBoutiqueStudentCount(studentCount)}`;

export const getBoutiqueEventPopoverPosition = (anchorRect: DOMRect, panelSize: { width: number; height: number }, viewportSize: { width: number; height: number }): BoutiqueEventPopoverPosition => {
	const { width: panelWidth, height: panelHeight } = panelSize;
	const { width: viewportWidth, height: viewportHeight } = viewportSize;

	const isPlacedRight = anchorRect.right + BoutiqueEventPopoverGap + panelWidth <= viewportWidth - BoutiqueEventPopoverViewportPadding;
	const preferredLeft = isPlacedRight ? anchorRect.right + BoutiqueEventPopoverGap : anchorRect.left - BoutiqueEventPopoverGap - panelWidth;

	const maxLeft = Math.max(viewportWidth - panelWidth - BoutiqueEventPopoverViewportPadding, BoutiqueEventPopoverViewportPadding);
	const left = Math.min(Math.max(preferredLeft, BoutiqueEventPopoverViewportPadding), maxLeft);

	const anchorCenter = anchorRect.top + anchorRect.height / 2;
	const maxTop = Math.max(viewportHeight - panelHeight - BoutiqueEventPopoverViewportPadding, BoutiqueEventPopoverViewportPadding);
	const top = Math.min(Math.max(anchorCenter - panelHeight / 2, BoutiqueEventPopoverViewportPadding), maxTop);

	const maxArrowTop = Math.max(panelHeight - BoutiqueEventPopoverArrowInset, BoutiqueEventPopoverArrowInset);
	const arrowTop = Math.min(Math.max(anchorCenter - top, BoutiqueEventPopoverArrowInset), maxArrowTop);

	return {
		left: Math.round(left),
		top: Math.round(top),
		arrowTop: Math.round(arrowTop),
		placement: isPlacedRight ? BoutiqueEventPopoverPlacement.RIGHT : BoutiqueEventPopoverPlacement.LEFT
	};
};
